import type { ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Anchor, Text } from '@mantine/core';
import { IconTool } from '@tabler/icons-react';
import { useCopy, supportLink } from '@/lib/settings';
import { EmptyState } from '@/components/ui/EmptyState';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import NotFound from '@/pages/NotFound';

/** Full-screen maintenance card (settings.maintenanceMode). Auth, privacy and terms stay reachable. */
export function MaintenanceCard() {
  const { t, settings } = useCopy();
  const { isAuthenticated } = useEnrichedAuth();
  const support = supportLink(settings);
  return (
    <div className="rides-main">
      <h1 className="sr-only">{t('app.name')} is paused</h1>
      <div style={{ paddingTop: 24 }}>
        <EmptyState
          icon={<IconTool size={30} />}
          title="We'll be back soon"
          secondaryAction={
            <Text component="div" ta="center" size="sm" c="dimmed">
              {support && <>Need help? <Anchor href={support.href}>Contact us</Anchor></>}
              {/* Signed-in phones have no footer, so the legal links live here too. */}
              {isAuthenticated && (
                <div>
                  <Anchor component={Link} to="/privacy" style={{ display: 'inline-block', padding: '10px 6px' }}>Privacy notice</Anchor>
                  {' · '}
                  <Anchor component={Link} to="/terms" style={{ display: 'inline-block', padding: '10px 6px' }}>Terms</Anchor>
                </div>
              )}
            </Text>
          }
        >
          {t('maintenance.message')}
        </EmptyState>
      </div>
    </div>
  );
}

/** Renders the maintenance card instead of the member flow while settings.maintenanceMode is on. */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const { settings } = useCopy();
  if (settings.maintenanceMode) return <MaintenanceCard />;
  return <>{children}</>;
}

/** Wizard steps redirect to /home while rider/driver registration is closed. */
export function RequireRegistrationOpen({ role, children }: { role: 'rider' | 'driver'; children: ReactNode }) {
  const { settings } = useCopy();
  const open = role === 'rider' ? settings.riderRegistrationOpen : settings.driverRegistrationOpen;
  if (!open) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

/** The campus-security view is only routable while settings.securityViewEnabled. */
export function RequireSecurityView({ children }: { children: ReactNode }) {
  const { settings } = useCopy();
  if (!settings.securityViewEnabled) return <NotFound />;
  return <>{children}</>;
}
